import React, { useState, useEffect } from 'react';
import { useStore } from 'react-redux';
import { Tooltip, IconButton, Grid, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Helmet from 'react-helmet';

import {
    Portlet,
    PortletBody,
    PortletHeader,
} from "../../partials/content/Portlet";
import { PayerDialog } from './PayerDialog';
import { PagedTable } from '../../common';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import classNames from 'classnames';
import { PayerImport } from './PayerImport';
import { EnableDialog } from './EnableDialog';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DependencyDialog } from './DependencyDialog';
import { ROLE_ADMIN, ROLE_PARTNER_ADMIN, ROLE_SUPERADMIN } from '../../store/constants/roles';
import GetAppIcon from '@mui/icons-material/GetApp';

const DEFAULT_SORT = [{ id: 'name', direction: 'asc' }];
const SHOW_SEARCH_FILTERS = true;

const useStyles = makeStyles((theme) => ({
    root: {
        margin: 'auto',
    },
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
    },

    icon: {
        fontSize: 17,
        marginTop: 1,
    }
}));

export default function PayerInfoComponent(props) {
    const store = useStore();
    const {
        payerData, payerFields,
        payerContracts, userRole, clientName,
        actions: {
            payersLanding,
            payersGetPayerContractsTiny,
            searchGetByName,
            searchReset,
            payerEditReset,
            resetPagedTable,
            setPayerInfoEditDialog,
            setPayerInfoEnableDialog,
            setList,
            setClaimDependencyDialog,
            setSelectedItems,
            downloadPayerReport,
            updatePayerHidden,
            bulkUpdatePayerHidden,
        }
    } = props;

    useEffect(() => {
        payersGetPayerContractsTiny();
    }, [payersGetPayerContractsTiny]);

    useEffect(() => {
        const pageName = {
            name: 'payerinfo-landing'
        }
        searchGetByName(pageName);

        return function cleanup() {
            searchReset();
            resetPagedTable(pageName.name);
            setSelectedItems([]);
        }
    }, [searchGetByName]);

    const [refresh, setRefresh] = useState(Promise.resolve());
    const [showHidden, setShowHidden] = useState(false);
    const isPricerPayer = window.location.pathname.includes('/pricer/');
    const [isImportDialogOpen, setImportDialog] = useState(false);
    const [currentGridState, setCurrentGridState] = useState({ filter: null, sort: DEFAULT_SORT });
    const [messageDialog, setMessageDialog] = useState({ open: false, title: '', message: '' });
    const pageLabel = isPricerPayer ? "Pricer Payer" : "Payer";
    const classes = useStyles();

    // useEffect(() => {
    //     getPayers();
    // }, [getPayers]);


    const handlePayerEdit = (payer) => {
        setPayerInfoEditDialog({ open: true, payer: payer, bulk: false });
    };

    // const handleCheckbox = (selectedRows, payer) => {
    //     updatePayerStatus(payer.id, !payer.active, handleRefresh);
    // };

    const handleAddPayer = () => {
        setPayerInfoEditDialog({ open: true, payer: null, bulk: false });
    }

    const handleClose = (shouldReloadGrid) => {
        payerEditReset();
        setPayerInfoEditDialog(null);
        setPayerInfoEnableDialog(null)
        if (shouldReloadGrid === true) {
            setList([]);
            handleRefresh();
        }
    }

    // const handleSelectAll = () => {
    //     const ids = payerData.map(({ id }) => id);
    //     const payload = {
    //         ids,
    //         enabled: !checkboxStatus,
    //     };
    //     selectDeselectAllPayers(payload, handleRefresh);
    // }


    function handleBulkEdit() {
        setPayerInfoEditDialog({ open: true, payer: null, bulk: true });
    }

    function handleImportPayerInfo() {
        setImportDialog(true);
    }

    function handleImportDialogClose() {
        setImportDialog(false);
        handleRefresh();
    }

    function handleBulkDelete() {
        setClaimDependencyDialog({ open: true });
    }

    function handleBulkHide() {
        const state = store.getState();
        const selectedItems = state.pagesV2['payerinfo-landing']?.selected || [];

        if (selectedItems.length === 0) {
            setMessageDialog({
                open: true,
                title: 'No Selection',
                message: 'No claim payers selected. Please select atleast one for hide'
            });
            return;
        }

        const selectedItemSet = new Set(selectedItems);

        const payersWithContracts = payerData.filter( p =>
                selectedItemSet.has(p.id) &&
                (p.payerContractId || p.pricerPayerContractId)
        );

        if (payersWithContracts.length > 0) {
            const payerNames = payersWithContracts.map(p => p.name).join(', ');
            setMessageDialog({
                open: true,
                title: 'Cannot Hide Payers',
                message: `Cannot hide the following payers with associated contracts: ${payerNames}. Please remove contracts first.`
            });
            return;
        }

        bulkUpdatePayerHidden({ ids: selectedItems, isHidden: true }, () => {
            setSelectedItems([]);
            handleRefresh();
        });
    }

    function handleBulkUnhide() {
        const state = store.getState();
        const selectedItems = state.pagesV2['payerinfo-landing']?.selected || [];

        if (selectedItems.length === 0) {
            setMessageDialog({
                open: true,
                title: 'No Selection',
                message: 'No claim payers selected. Please select atleast one for unhide'
            });
            return;
        }

        bulkUpdatePayerHidden({ ids: selectedItems, isHidden: false }, () => {
            setSelectedItems([]);
            handleRefresh();
        });
    }

    function tableLevelActions() {
        return (
            <>
                <Grid item>
                    <Tooltip title="Export">
                        <IconButton
                            aria-label="export"
                            size='small'
                            onClick={handleExportPayers}
                        >
                            <GetAppIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <Tooltip title={`Import ${pageLabel} Info`}>
                        <IconButton aria-label="download" size='small'
                            onClick={handleImportPayerInfo}
                        >
                            <i className="fas fa-upload"></i>
                        </IconButton>
                    </Tooltip>
                </Grid>
                {(userRole === ROLE_SUPERADMIN || userRole === ROLE_PARTNER_ADMIN || userRole === ROLE_ADMIN) &&
                    <Grid item>
                        <Tooltip title={`Delete Claim ${pageLabel}s`}>
                            <IconButton aria-label="delete" size='small'
                                onClick={handleBulkDelete}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                }
                <Grid item>
                    <Tooltip title="Hide Selected Payers">
                        <IconButton aria-label="hide" size='small' onClick={handleBulkHide}>
                            <VisibilityOffIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                {showHidden &&
                    <Grid item>
                        <Tooltip title={`Unhide Selected ${pageLabel}s`}>
                            <IconButton aria-label="unhide" size='small' onClick={handleBulkUnhide}>
                                <VisibilityIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                }
                <Grid item>
                    <Tooltip title="Edit">
                        <IconButton aria-label="edit" size='small' onClick={() => handleBulkEdit()}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <Tooltip title="Enable">
                        <IconButton aria-label="enable" size='small' onClick={() => handlePayerConfirmation(true)}>
                            <CheckCircleIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <Tooltip title="Disable">
                        <IconButton aria-label="disable" size='small' onClick={() => handlePayerConfirmation(false)}>
                            <i className={classNames("fa fa-solid fa-ban", classes.icon)}></i>
                        </IconButton>
                    </Tooltip>
                </Grid>
            </>
        );
    }

    function handleLoad(sort, filter, pagination) {
        setCurrentGridState({ filter, sort });
        payersLanding(pagination, sort, filter, showHidden, isPricerPayer);
    }

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }

    function handleToggleShowHidden() {
        setShowHidden(prev => !prev);
    }

    useEffect(() => {
        handleRefresh();
    }, [showHidden]);

    function handlePayerConfirmation(isEnable) {
        setPayerInfoEnableDialog({
            open: true,
            isEnableClicked: isEnable
        })
    }

    function handleExportPayers() {
        const payload = {
            page: null,
            filter: currentGridState.filter || null,
            sort: currentGridState.sort || [],
        };

        downloadPayerReport(payload, downloadCallback, isPricerPayer);
    }

    function downloadCallback(blob) {
        const fileName = `${clientName}_Claim${pageLabel}s.csv`;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    function handleTogglePayerHidden(rowData) {
        updatePayerHidden({ id: rowData.id, isHidden: !rowData.isHidden }, handleRefresh);
    }

    function payerInfoActions(rowData) {
        const hasContract = rowData.payerContractId || rowData.pricerPayerContractId;
        const canToggleHide = rowData.isHidden || !hasContract; // Can always unhide, can only hide if no contract

        return (
            <div className='d-flex'>
                <IconButton
                    color="primary"
                    aria-label="Edit"
                    onClick={() => handlePayerEdit(rowData)}
                    size="large">
                    <Tooltip title="Edit">
                        <EditIcon />
                    </Tooltip>
                </IconButton>
                <IconButton
                    aria-label="Toggle Hidden"
                    onClick={() => handleTogglePayerHidden(rowData)}
                    disabled={!canToggleHide}
                    size="large">
                    <Tooltip title={
                        !canToggleHide
                            ? "Cannot hide payer with associated contract"
                            : rowData.isHidden ? `Unhide ${pageLabel}` : `Hide ${pageLabel}`
                    }>
                        {rowData.isHidden
                            ? <VisibilityOffIcon color="error" />
                            : <VisibilityIcon color={canToggleHide ? "primary" : "disabled"} />
                        }
                    </Tooltip>
                </IconButton>
            </div>
        );
    }

    return (
        <>
            <Helmet title={`Claim ${pageLabel}s`} />
            <Portlet>
                <PortletHeader
                    title={`Claim ${pageLabel}s`}
                    handleRefresh={handleRefresh}
                    showSearchFilters={SHOW_SEARCH_FILTERS}
                    showSearchFiltersDropdown={false}
                    name="payerinfo-landing"
                    includeAdvanceFilters
                    toolbar={
                        <FormControlLabel
                            sx={{
                                position: 'absolute',
                                right: 205,
                                top: '62%',
                                transform: 'translateY(-50%)',
                                zIndex: 1,
                            }}
                            control={
                                <Switch
                                    checked={showHidden}
                                    onChange={handleToggleShowHidden}
                                    color="primary"
                                />
                            }
                            label={`Show Hidden ${pageLabel}s`}
                        />
                    }
                />
                <PortletBody>
                    <div className="row">
                        <div className="col-md-12">
                            <PagedTable
                                name="payerinfo-landing"
                                fields={payerFields}
                                data={payerData}
                                onLoad={handleLoad}
                                refresh={refresh}
                                defaultSort={DEFAULT_SORT}
                                onAdd={handleAddPayer}
                                tableLevelActions={tableLevelActions}
                                hasCheckbox={true}
                                showSelectAll={true}
                                title={`Claim ${pageLabel}`}
                                criteriasSaveEnabled={true}
                                showSearchFilters={SHOW_SEARCH_FILTERS}
                                hasActions={true}
                                renderActionColumn={payerInfoActions}
                                shouldRenderLazy={true}
                            />
                        </div>
                    </div>
                </PortletBody>
                <PayerDialog
                    payerContracts={payerContracts}
                    handleClose={handleClose}
                    isPricerPayer={isPricerPayer} />
            </Portlet>
            <EnableDialog handleClose={handleClose}  />
            <PayerImport open={isImportDialogOpen} handleClose={handleImportDialogClose} isPricerPayer={isPricerPayer} />
            <DependencyDialog handleClose={handleClose} />
            <Dialog
                open={messageDialog.open}
                onClose={() => setMessageDialog({ open: false, title: '', message: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{messageDialog.title}</DialogTitle>
                <DialogContent>
                    <Typography>{messageDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setMessageDialog({ open: false, title: '', message: '' })}
                        variant="contained"
                        color="primary"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}